import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';

// Context Providers
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';

// Layout Components
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { CartDrawer } from './components/layout/CartDrawer';
import { ScrollToTop } from './components/layout/ScrollToTop';

// Route Protection
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';

// Admin Pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminReservationPage from './pages/admin/AdminReservationPage';

// Pages
import HomePage from './pages/HomePage';
import MenuPage from './pages/MenuPage';
import ProductPage from './pages/ProductPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AccountPage from './pages/AccountPage';
import AboutPage from './pages/AboutPage';
import GalleryPage from './pages/GalleryPage';
import ContactPage from './pages/ContactPage';
import ReservationPage from './pages/ReservationPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import NewsletterPage from './pages/NewsletterPage';
import OrderDetailsPage from './pages/OrderDetailsPage';
import ConfidentialitePage from './pages/ConfidentialitePage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import VerificationEmailPage from './pages/VerificationEmailPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import AdminNewsletterPage from './pages/admin/AdminNewsletterPage';
import SiteBanner from "./components/ui/SiteBanner";

// Cookie Banner Components
import CookieBanner from './components/CookieBanner';

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!window.gtag) return;

    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_path: location.pathname + location.search,
      page_location: window.location.href,
    });
  }, [location]);

  return null;
}

// Layout wrapper component
const Layout = ({ children }) => {
  return (
    <div className="flex flex-col min-h-screen bg-warm-alabaster">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
      <CookieBanner />
      <CartDrawer />
      <SiteBanner />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AnalyticsTracker />

      <AuthProvider>
        <CartProvider>
          <ScrollToTop />
          <Layout>
            <Routes>
              {/* Public Pages */}
              <Route path="/" element={<HomePage />} />
              <Route path="/menu" element={<MenuPage />} />
              <Route path="/produit/:slug" element={<ProductPage />} />
              <Route path="/panier" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/a-propos" element={<AboutPage />} />
              <Route path="/galerie" element={<GalleryPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/reservation" element={<ReservationPage />} />
              <Route path="/newsletter" element={<NewsletterPage />} />
              <Route path="/commande/:id" element={<OrderDetailsPage />} />
              <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
              <Route path="/confidentialite" element={<ConfidentialitePage />} />
              <Route path="/verification-email" element={<VerificationEmailPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />

              {/* Auth Pages */}
              <Route path="/connexion" element={<LoginPage />} />
              <Route path="/inscription" element={<RegisterPage />} />
              <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />

              {/* Account */}
              <Route
                path="/mon-compte"
                element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                }
              />

              {/* Admin */}
              <Route element={<AdminRoute />}>
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                <Route path="/admin/orders" element={<AdminOrdersPage />} />
                <Route path="/admin/products" element={<AdminProductsPage />} />
                <Route path="/admin/users" element={<AdminUsersPage />} />
                <Route path="/admin/settings" element={<AdminSettingsPage />} />
                <Route path="/admin/reservations" element={<AdminReservationPage />} />
                <Route path="/admin/newsletter" element={<AdminNewsletterPage />} />
              </Route>

              {/* Payment */}
              <Route path="/paiement-reussi" element={<PaymentSuccessPage />} />
              <Route path="/paiement-annule" element={<PaymentCancelPage />} />

              {/* 404 */}
              <Route path="*" element={<HomePage />} />
            </Routes>
          </Layout>

          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: '#FFFCF8',
                border: '1px solid #E6E1D6',
                color: '#2A2A2A',
              },
              className: 'font-sans',
            }}
          />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;